/**
 * TimeSelect Component (#1263)
 *
 * A <select> dropdown with 15-minute interval times.
 * Works on all browsers including iOS Safari (unlike step="900" on time inputs).
 */

import { Clock } from 'lucide-react';
import { cn } from '../../lib/utils';

interface TimeSelectProps {
  value: string; // "HH:MM" format (24-hour)
  onChange: (time: string) => void;
  className?: string;
  id?: string;
  required?: boolean;
}

// Generate time options in 15-minute intervals
function generateTimeOptions(): { value: string; label: string; group: string }[] {
  const options: { value: string; label: string; group: string }[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      // Format label as 12-hour time
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;

      // Determine group
      let group: string;
      if (hour >= 5 && hour < 12) {
        group = 'Morning';
      } else if (hour >= 12 && hour < 17) {
        group = 'Afternoon';
      } else if (hour >= 17 && hour < 21) {
        group = 'Evening';
      } else {
        group = 'Night';
      }

      options.push({ value, label, group });
    }
  }

  return options;
}

const TIME_OPTIONS = generateTimeOptions();

// Group options by time of day
const GROUPED_OPTIONS = TIME_OPTIONS.reduce((acc, option) => {
  if (!acc[option.group]) {
    acc[option.group] = [];
  }
  acc[option.group].push(option);
  return acc;
}, {} as Record<string, typeof TIME_OPTIONS>);

// Night spans midnight (21:00–04:45). The 0–23 generation loop pushes
// hour 0–4 entries before hour 21–23 entries, which reads as if 9 PM
// through 11:45 PM are missing (#1639). Sort so 21:00–23:45 come first
// (continuing chronologically from Evening), then 12:00–04:45.
if (GROUPED_OPTIONS['Night']) {
  GROUPED_OPTIONS['Night'].sort((a, b) => {
    const [hA, mA] = a.value.split(':').map(Number);
    const [hB, mB] = b.value.split(':').map(Number);
    const keyA = (hA < 21 ? hA + 24 : hA) * 100 + mA;
    const keyB = (hB < 21 ? hB + 24 : hB) * 100 + mB;
    return keyA - keyB;
  });
}

// Order groups logically for event creation (morning runs are common)
const GROUP_ORDER = ['Morning', 'Afternoon', 'Evening', 'Night'];

export default function TimeSelect({
  value,
  onChange,
  className,
  id,
  required
}: TimeSelectProps) {
  return (
    <div className={cn('relative', className)}>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full appearance-none bg-oro-surface-alt border border-oro-border rounded-lg pl-10 pr-4 py-3 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent cursor-pointer"
      >
        <option value="">Select time</option>
        {GROUP_ORDER.map((group) => (
          <optgroup key={group} label={group}>
            {GROUPED_OPTIONS[group]?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}
